// src/components/Modules.jsx
// All data-entry and reporting modules in one file.

import { useState } from "react";
import { OILS, SKUS, SKU_LITRES } from "../constants";
import { fmt, fmtL, fmtKg, today, OilDot, PayPill, AlertBar, EmptyState, S, TabRow } from "../utils/ui";
import { ConfirmPurchase, ConfirmBatch, ConfirmTransfer, ConfirmSale, ConfirmCakeSale } from "./ConfirmSave";

// ─── SHARED PIECES ────────────────────────────────────────────────────────────

function HistoryCard({ children }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 10, border: "1px solid #E8E4DC",
      padding: "10px 14px", marginBottom: 8,
    }}>
      {children}
    </div>
  );
}

function SkuSelector({ value, onChange }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
      {SKUS.map(s => (
        <div key={s} onClick={() => onChange(s)} style={S.skuBtn(value === s)}>{s}</div>
      ))}
    </div>
  );
}

function PaySelector({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
      {["Cash", "UPI", "Credit"].map(m => (
        <div key={m} onClick={() => onChange(m)} style={S.payBtn(value === m, m)}>{m}</div>
      ))}
    </div>
  );
}

function SaveMsg({ msg }) {
  if (!msg) return null;
  return <div style={S.successMsg}>✓ {msg}</div>;
}

// ─── PURCHASE ─────────────────────────────────────────────────────────────────

export function Purchase({ state, actions, user }) {
  const [oil, setOil] = useState(OILS[0]);
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("kg");
  const [price, setPrice] = useState("");
  const [date, setDate] = useState(today());
  const [supplier, setSupplier] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const kgs = unit === "quintal" ? Number(qty) * 100 : Number(qty);
  const priceVal = Number(price) || state.prices.raw[oil] || 80;
  const totalVal = kgs * priceVal;

  const handleSave = () => {
    if (!qty || kgs <= 0) return;
    setConfirm({ oil, kgs, unit, price: priceVal, date, supplier });
  };

  const confirmSave = async () => {
    setSaving(true);
    await actions.addPurchase(confirm, user);
    setConfirm(null);
    setQty(""); setSupplier(""); setPrice("");
    setMsg("Purchase saved!"); setTimeout(() => setMsg(""), 2500);
    setSaving(false);
  };

  return (
    <div style={{ padding: "12px 14px" }}>
      {confirm && (
        <ConfirmPurchase
          data={confirm}
          onConfirm={confirmSave}
          onCancel={() => setConfirm(null)}
          saving={saving}
        />
      )}
      <div style={S.card}>
        <div style={S.cardTitle}>Log raw material purchase</div>

        <label style={S.label}>Oil seed / raw material</label>
        <select style={S.select} value={oil} onChange={e => setOil(e.target.value)}>
          {OILS.map(o => <option key={o}>{o}</option>)}
        </select>

        <label style={S.label}>Quantity</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input style={{ ...S.input, flex: 2, marginBottom: 0 }}
            type="number" placeholder="Amount"
            value={qty} onChange={e => setQty(e.target.value)} />
          <select style={{ ...S.select, flex: 1, marginBottom: 8 }}
            value={unit} onChange={e => setUnit(e.target.value)}>
            <option>kg</option>
            <option>quintal</option>
          </select>
        </div>
        {qty && <div style={{ fontSize: 12, color: "#27500A", marginBottom: 8 }}>= {kgs} kg</div>}

        <label style={S.label}>Price per kg (₹)</label>
        <input style={S.input} type="number"
          placeholder={`Current rate: ₹${state.prices.raw[oil]}/kg`}
          value={price} onChange={e => setPrice(e.target.value)} />

        <label style={S.label}>Supplier (optional)</label>
        <input style={S.input} placeholder="Supplier name or contact"
          value={supplier} onChange={e => setSupplier(e.target.value)} />

        <label style={S.label}>Date</label>
        <input style={S.input} type="date" value={date} onChange={e => setDate(e.target.value)} />

        {kgs > 0 && (
          <div style={{ background: "#F0F9E8", borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 13 }}>
            Total purchase value: <strong>{fmt(totalVal)}</strong>
            <div style={{ color: "#888", marginTop: 2 }}>{kgs} kg × ₹{priceVal}/kg</div>
          </div>
        )}

        <SaveMsg msg={msg} />
        <button style={S.btn()} onClick={handleSave}>
          Review & Save
        </button>
      </div>

      <div style={S.sectionTitle}>Purchase history</div>
      {state.purchases.length === 0
        ? <EmptyState text="No purchases yet" />
        : [...state.purchases].reverse().map(p => (
          <HistoryCard key={p.ts}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <OilDot oil={p.oil} /><strong>{p.oil}</strong>
              </div>
              <strong style={{ fontSize: 13 }}>{fmt(Number(p.kgs) * Number(p.price))}</strong>
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {p.kgs} kg · ₹{p.price}/kg · {p.date} · {p.by}
              {p.supplier && ` · ${p.supplier}`}
            </div>
          </HistoryCard>
        ))}
    </div>
  );
}

// ─── PRODUCTION ───────────────────────────────────────────────────────────────

export function Production({ state, actions, user }) {
  const [oil, setOil] = useState(OILS[0]);
  const [rawUsed, setRawUsed] = useState("");
  const [date, setDate] = useState(today());
  const [editYield, setEditYield] = useState(false);
  const [yOil, setYOil] = useState("");
  const [yCake, setYCake] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const y = state.yields[oil] || { oil: 40, cake: 55 };
  const kgs = Number(rawUsed);
  const oilLitres = kgs * (y.oil / 100);
  const cakeKg    = kgs * (y.cake / 100);
  const available = state.rawStock[oil] || 0;

  const saveYield = async () => {
    const o = Number(yOil), c = Number(yCake);
    if (o <= 0 || c <= 0 || o + c > 100) return;
    await actions.updateYields(oil, o, c, user);
    setEditYield(false);
  };

  const handleSave = () => {
    if (!rawUsed || kgs <= 0 || kgs > available) return;
    setConfirm({ oil, rawUsed: kgs, oilLitres, cakeKg, yieldOil: y.oil, yieldCake: y.cake, date });
  };

  const confirmSave = async () => {
    setSaving(true);
    await actions.addBatch(confirm, user);
    setConfirm(null);
    setRawUsed("");
    setMsg("Batch logged!"); setTimeout(() => setMsg(""), 2500);
    setSaving(false);
  };

  return (
    <div style={{ padding: "12px 14px" }}>
      {confirm && (
        <ConfirmBatch
          data={confirm}
          onConfirm={confirmSave}
          onCancel={() => setConfirm(null)}
          saving={saving}
        />
      )}
      <div style={S.card}>
        <div style={S.cardTitle}>Log production batch</div>

        <label style={S.label}>Oil seed</label>
        <select style={S.select} value={oil} onChange={e => { setOil(e.target.value); setEditYield(false); }}>
          {OILS.map(o => <option key={o}>{o}</option>)}
        </select>

        <div style={{ background: "#F7F4EE", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#555" }}>
              Yield — Oil: <strong>{y.oil}%</strong> · Cake: <strong>{y.cake}%</strong>
            </span>
            <span style={{ fontSize: 12, color: "#27500A", cursor: "pointer", fontWeight: 600 }}
              onClick={() => { setEditYield(!editYield); setYOil(y.oil); setYCake(y.cake); }}>
              {editYield ? "Cancel" : "Edit"}
            </span>
          </div>
          {editYield && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Oil yield %</label>
                  <input style={{ ...S.input, marginBottom: 0 }} type="number"
                    value={yOil} onChange={e => setYOil(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Cake yield %</label>
                  <input style={{ ...S.input, marginBottom: 0 }} type="number"
                    value={yCake} onChange={e => setYCake(e.target.value)} />
                </div>
              </div>
              {Number(yOil) + Number(yCake) > 100 && (
                <div style={{ color: "#A32D2D", fontSize: 12, marginBottom: 6 }}>
                  Oil + Cake cannot exceed 100%
                </div>
              )}
              <button style={{ ...S.btn("#185FA5"), padding: "8px" }} onClick={saveYield}>
                Save yields for {oil}
              </button>
            </div>
          )}
        </div>

        <label style={S.label}>Raw material used in this batch (kg)</label>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
          Available at plant: <strong>{fmtKg(available)}</strong>
        </div>
        <input style={S.input} type="number" placeholder="Enter kg used"
          value={rawUsed} onChange={e => setRawUsed(e.target.value)} />

        {kgs > 0 && (
          <div style={{ background: "#F0F9E8", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 13 }}>
              <div><div style={{ color: "#888" }}>Oil produced</div><strong>{fmtL(oilLitres)}</strong></div>
              <div><div style={{ color: "#888" }}>Cake produced</div><strong>{fmtKg(cakeKg)}</strong></div>
              <div><div style={{ color: "#888" }}>Loss</div><strong>{fmtKg(kgs * (1 - y.oil/100 - y.cake/100))}</strong></div>
            </div>
          </div>
        )}
        {kgs > available && <div style={S.errBox}>⚠ Not enough raw material (only {fmtKg(available)} available)</div>}

        <label style={S.label}>Date</label>
        <input style={S.input} type="date" value={date} onChange={e => setDate(e.target.value)} />

        <SaveMsg msg={msg} />
        <button style={S.btn(kgs > available ? "#888" : "#27500A")}
          onClick={handleSave} disabled={kgs > available}>
          Review & Save
        </button>
      </div>

      <div style={S.sectionTitle}>Batch history</div>
      {state.batches.length === 0
        ? <EmptyState text="No batches yet" />
        : [...state.batches].reverse().map(b => (
          <HistoryCard key={b.ts}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <OilDot oil={b.oil} /><strong>{b.oil}</strong>
              </div>
              <span style={{ fontSize: 11, color: "#888" }}>{b.date}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, fontSize: 12 }}>
              <div><div style={{ color: "#888" }}>Used</div><strong>{fmtKg(b.rawUsed)}</strong></div>
              <div><div style={{ color: "#888" }}>Oil out</div><strong>{fmtL(b.oilLitres)}</strong></div>
              <div><div style={{ color: "#888" }}>Cake out</div><strong>{fmtKg(b.cakeKg)}</strong></div>
            </div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
              By {b.by} · Yield: {b.yieldOil}% oil, {b.yieldCake}% cake
            </div>
          </HistoryCard>
        ))}
    </div>
  );
}

// ─── TRANSFER ─────────────────────────────────────────────────────────────────

export function Transfer({ state, actions, user }) {
  const [oil, setOil] = useState(OILS[0]);
  const [sku, setSku] = useState(SKUS[2]);
  const [count, setCount] = useState("");
  const [date, setDate] = useState(today());
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const litres   = Number(count) * SKU_LITRES[sku];
  const available = state.oilPlant[oil] || 0;
  const value    = litres * (state.prices.oil?.[oil]?.["1L"] || 0);

  const handleSave = () => {
    if (!count || litres <= 0 || litres > available) return;
    setConfirm({ oil, sku, count: Number(count), litres, value, date });
  };

  const confirmSave = async () => {
    setSaving(true);
    await actions.addTransfer(confirm, user);
    setConfirm(null);
    setCount("");
    setMsg("Transfer logged!"); setTimeout(() => setMsg(""), 2500);
    setSaving(false);
  };

  return (
    <div style={{ padding: "12px 14px" }}>
      {confirm && (
        <ConfirmTransfer
          data={confirm}
          onConfirm={confirmSave}
          onCancel={() => setConfirm(null)}
          saving={saving}
        />
      )}
      <AlertBar
        text="Transfer moves oil from plant stock to shop stock. Vaibhav should log this when dispatching."
        type="info"
      />
      <div style={S.card}>
        <div style={S.cardTitle}>Transfer oil — plant → shop</div>

        <label style={S.label}>Oil type</label>
        <select style={S.select} value={oil} onChange={e => setOil(e.target.value)}>
          {OILS.map(o => <option key={o}>{o}</option>)}
        </select>

        <label style={S.label}>Pack size (SKU)</label>
        <SkuSelector value={sku} onChange={setSku} />

        <label style={S.label}>Number of units (bottles / cans)</label>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
          Oil available at plant: <strong>{fmtL(available)}</strong>
        </div>
        <input style={S.input} type="number" placeholder="How many bottles/cans?"
          value={count} onChange={e => setCount(e.target.value)} />

        {Number(count) > 0 && (
          <div style={{ background: "#F0F9E8", borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 13 }}>
            <div>Volume: <strong>{fmtL(litres)}</strong></div>
            <div>Estimated value: <strong>{fmt(value)}</strong></div>
            <div style={{ color: "#888" }}>{count} × {sku} bottles/cans</div>
          </div>
        )}
        {litres > available && (
          <div style={S.errBox}>⚠ Not enough oil at plant ({fmtL(available)} available)</div>
        )}

        <label style={S.label}>Date</label>
        <input style={S.input} type="date" value={date} onChange={e => setDate(e.target.value)} />

        <SaveMsg msg={msg} />
        <button style={S.btn(litres > available ? "#888" : "#27500A")}
          onClick={handleSave} disabled={litres > available}>
          Review & Save
        </button>
      </div>

      <div style={S.sectionTitle}>Transfer history</div>
      {state.transfers.length === 0
        ? <EmptyState text="No transfers yet" />
        : [...state.transfers].reverse().map(t => (
          <HistoryCard key={t.ts}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <OilDot oil={t.oil} /><strong>{t.oil}</strong>
                <span style={{ fontSize: 12, color: "#888", marginLeft: 6 }}>· {t.sku}</span>
              </div>
              <strong style={{ fontSize: 13 }}>{fmt(t.value)}</strong>
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {t.count} units · {fmtL(t.litres)} · {t.date} · {t.by}
            </div>
          </HistoryCard>
        ))}
    </div>
  );
}

// ─── SALES ────────────────────────────────────────────────────────────────────

function OilSaleForm({ state, actions, user }) {
  const [oil, setOil] = useState(OILS[0]);
  const [sku, setSku] = useState(SKUS[2]);
  const [qty, setQty] = useState("");
  const [mode, setMode] = useState("Cash");
  const [location, setLocation] = useState(user.plant ? "Plant" : "Shop");
  const [customPrice, setCustomPrice] = useState("");
  const [date, setDate] = useState(today());
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const litres      = Number(qty) * SKU_LITRES[sku];
  const pricePerSku = Number(customPrice) || state.prices.oil?.[oil]?.[sku] || 0;
  const amount      = Number(qty) * pricePerSku;
  const stockAvail  = location === "Shop" ? (state.oilShop[oil] || 0) : (state.oilPlant[oil] || 0);
  const locOptions  = [...(user.plant ? ["Plant"] : []), ...(user.shop ? ["Shop"] : [])];

  const handleSave = () => {
    if (!qty || litres <= 0 || litres > stockAvail) return;
    setConfirm({ oil, sku, qty: Number(qty), litres, amount, mode, location, date, buyerName, buyerPhone });
  };

  const confirmSave = async () => {
    setSaving(true);
    await actions.addSale(confirm, user);
    setConfirm(null);
    setQty(""); setCustomPrice(""); setBuyerName(""); setBuyerPhone("");
    setMsg("Sale logged!"); setTimeout(() => setMsg(""), 2500);
    setSaving(false);
  };

  return (
    <>
      {confirm && (
        <ConfirmSale
          data={confirm}
          onConfirm={confirmSave}
          onCancel={() => setConfirm(null)}
          saving={saving}
        />
      )}
      <label style={S.label}>Oil type</label>
      <select style={S.select} value={oil} onChange={e => setOil(e.target.value)}>
        {OILS.map(o => <option key={o}>{o}</option>)}
      </select>

      <label style={S.label}>Pack size</label>
      <SkuSelector value={sku} onChange={setSku} />

      <label style={S.label}>Quantity (units sold)</label>
      <input style={S.input} type="number" placeholder="Number of bottles/cans"
        value={qty} onChange={e => setQty(e.target.value)} />

      <label style={S.label}>Price per {sku} (₹) — leave blank for default</label>
      <input style={S.input} type="number"
        placeholder={`Default: ₹${state.prices.oil?.[oil]?.[sku] || 0}`}
        value={customPrice} onChange={e => setCustomPrice(e.target.value)} />

      {locOptions.length > 1 && (
        <>
          <label style={S.label}>Sale from</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {locOptions.map(l => (
              <div key={l} onClick={() => setLocation(l)}
                style={{ ...S.skuBtn(location === l), padding: "8px 20px" }}>
                {l}
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
        Stock at {location}: <strong>{fmtL(stockAvail)}</strong>
      </div>

      <label style={S.label}>Payment mode</label>
      <PaySelector value={mode} onChange={setMode} />

      {/* Buyer info — required for credit, optional for all */}
      <label style={S.label}>
        Buyer name
        {mode === "Credit"
          ? <span style={{ color: "#A32D2D", marginLeft: 4 }}>* required for credit</span>
          : <span style={{ color: "#aaa" }}> (optional — helps build customer database)</span>}
      </label>
      <input style={{ ...S.input, borderColor: mode === "Credit" && !buyerName ? "#F5C4B3" : "#DDD9D0" }}
        placeholder="e.g. Suresh Kumar"
        value={buyerName} onChange={e => setBuyerName(e.target.value)} />

      <label style={S.label}>Buyer phone <span style={{ color: "#aaa" }}>(optional)</span></label>
      <input style={S.input} type="tel" placeholder="10-digit mobile number"
        value={buyerPhone} onChange={e => setBuyerPhone(e.target.value)} />

      <label style={S.label}>Date</label>
      <input style={S.input} type="date" value={date} onChange={e => setDate(e.target.value)} />

      {Number(qty) > 0 && (
        <div style={{ background: "#F0F9E8", borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 13 }}>
          <div>Volume: <strong>{fmtL(litres)}</strong></div>
          <div>Amount: <strong>{fmt(amount)}</strong></div>
          <div style={{ color: "#888" }}>{qty} × {sku} @ ₹{pricePerSku} each</div>
          {buyerName && <div style={{ color: "#888", marginTop: 4 }}>Buyer: {buyerName}</div>}
        </div>
      )}
      {litres > stockAvail && (
        <div style={S.errBox}>⚠ Not enough stock at {location} (only {fmtL(stockAvail)} available)</div>
      )}
      {mode === "Credit" && !buyerName && qty && (
        <div style={{ ...S.errBox, color: "#854F0B" }}>⚠ Please enter buyer name for credit sales</div>
      )}

      <SaveMsg msg={msg} />
      <button style={S.btn(litres > stockAvail || (mode === "Credit" && !buyerName) ? "#888" : "#27500A")}
        onClick={handleSave} disabled={litres > stockAvail || (mode === "Credit" && !buyerName)}>
        Review & Save
      </button>
    </>
  );
}

function CakeSaleForm({ state, actions, user }) {
  const [oil, setOil] = useState(OILS[0]);
  const [kg, setKg] = useState("");
  const [mode, setMode] = useState("Cash");
  const [customPrice, setCustomPrice] = useState("");
  const [date, setDate] = useState(today());
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const kgVal    = Number(kg);
  const priceVal = Number(customPrice) || state.prices.cake[oil] || 30;
  const amount   = kgVal * priceVal;
  const available = state.cakeStock[oil] || 0;

  const handleSave = () => {
    if (!kg || kgVal <= 0 || kgVal > available) return;
    setConfirm({ oil, kg: kgVal, amount, mode, date, buyerName, buyerPhone });
  };

  const confirmSave = async () => {
    setSaving(true);
    await actions.addCakeSale(confirm, user);
    setConfirm(null);
    setKg(""); setCustomPrice(""); setBuyerName(""); setBuyerPhone("");
    setMsg("Sale logged!"); setTimeout(() => setMsg(""), 2500);
    setSaving(false);
  };

  return (
    <>
      {confirm && (
        <ConfirmCakeSale
          data={confirm}
          onConfirm={confirmSave}
          onCancel={() => setConfirm(null)}
          saving={saving}
        />
      )}
      <label style={S.label}>Oil type (cake from)</label>
      <select style={S.select} value={oil} onChange={e => setOil(e.target.value)}>
        {OILS.map(o => <option key={o}>{o}</option>)}
      </select>

      <label style={S.label}>Quantity (kg)</label>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
        Available: <strong>{fmtKg(available)}</strong>
      </div>
      <input style={S.input} type="number" placeholder="kg sold"
        value={kg} onChange={e => setKg(e.target.value)} />

      <label style={S.label}>Price per kg (₹)</label>
      <input style={S.input} type="number"
        placeholder={`Default: ₹${state.prices.cake[oil]}/kg`}
        value={customPrice} onChange={e => setCustomPrice(e.target.value)} />

      <label style={S.label}>Buyer name <span style={{ color: "#aaa" }}>(optional)</span></label>
      <input style={S.input} placeholder="e.g. Ramesh Patil / Farmer"
        value={buyerName} onChange={e => setBuyerName(e.target.value)} />

      <label style={S.label}>Buyer phone <span style={{ color: "#aaa" }}>(optional)</span></label>
      <input style={S.input} type="tel" placeholder="10-digit mobile number"
        value={buyerPhone} onChange={e => setBuyerPhone(e.target.value)} />

      <label style={S.label}>Payment mode</label>
      <PaySelector value={mode} onChange={setMode} />

      <label style={S.label}>Date</label>
      <input style={S.input} type="date" value={date} onChange={e => setDate(e.target.value)} />

      {kgVal > 0 && (
        <div style={{ background: "#F0F9E8", borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 13 }}>
          Amount: <strong>{fmt(amount)}</strong> ({kg} kg × ₹{priceVal}/kg)
          {buyerName && <div style={{ color: "#888", marginTop: 4 }}>Buyer: {buyerName}</div>}
        </div>
      )}
      {kgVal > available && (
        <div style={S.errBox}>⚠ Not enough cake stock (only {fmtKg(available)} available)</div>
      )}

      <SaveMsg msg={msg} />
      <button style={S.btn(kgVal > available ? "#888" : "#27500A")}
        onClick={handleSave} disabled={kgVal > available}>
        Review & Save
      </button>
    </>
  );
}

export function Sales({ state, actions, user }) {
  const [tab, setTab] = useState("oil");

  const tabs = [{ id: "oil", label: "Oil Sale" }];
  if (user.plant) tabs.push({ id: "cake", label: "Cake Sale" });

  return (
    <div style={{ padding: "12px 14px" }}>
      <TabRow tabs={tabs} active={tab} onChange={setTab} />

      <div style={S.card}>
        <div style={S.cardTitle}>{tab === "oil" ? "Log oil sale" : "Log cake sale (plant only)"}</div>
        {tab === "oil"
          ? <OilSaleForm state={state} actions={actions} user={user} />
          : user.plant
            ? <CakeSaleForm state={state} actions={actions} user={user} />
            : <div style={{ color: "#888", textAlign: "center", padding: "16px 0" }}>
                Cake sales are logged from the plant only.
              </div>
        }
      </div>

      <div style={S.sectionTitle}>Recent sales</div>
      {state.sales.length === 0
        ? <EmptyState text="No sales yet" />
        : [...state.sales].reverse().slice(0, 15).map(s => (
          <HistoryCard key={s.ts}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                <OilDot oil={s.oil} />
                <strong>{s.oil}</strong>
                <span style={{ fontSize: 12, color: "#888", marginLeft: 4 }}>· {s.sku}</span>
                <PayPill mode={s.mode} />
              </div>
              <strong style={{ fontSize: 13 }}>{fmt(s.amount)}</strong>
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {s.qty} units · {fmtL(s.litres)} · {s.location} · {s.by} · {s.date}
            </div>
          </HistoryCard>
        ))}
    </div>
  );
}

// ─── REPORTS ──────────────────────────────────────────────────────────────────

export function Reports({ state }) {
  const [period, setPeriod] = useState("today");
  const todayStr = today();
  const filt = s => period === "today" ? s.date === todayStr : true;

  const filtSales  = state.sales.filter(filt);
  const filtCake   = state.cakeSales.filter(filt);
  const shopSales  = filtSales.filter(s => s.location === "Shop");
  const plantSales = filtSales.filter(s => s.location === "Plant");
  const totOil     = filtSales.reduce((a, s) => a + s.amount, 0);
  const totCake    = filtCake.reduce((a, s) => a + s.amount, 0);

  return (
    <div style={{ padding: "12px 14px" }}>
      <TabRow
        tabs={[{ id: "today", label: "Today" }, { id: "all", label: "All time" }]}
        active={period} onChange={setPeriod}
      />

      <div style={S.card}>
        <div style={S.cardTitle}>Sales summary</div>
        {[
          ["Oil — Shop",   shopSales.reduce((a, s) => a + s.amount, 0),  "#555", 400],
          ["Oil — Plant",  plantSales.reduce((a, s) => a + s.amount, 0), "#555", 400],
          ["Cake sales",   totCake, "#555", 400],
          ["Total revenue", totOil + totCake, "#27500A", 700],
        ].map(([l, v, c, w]) => (
          <div key={l} style={{ ...S.row, fontWeight: w }}>
            <span style={{ fontSize: 13, color: c }}>{l}</span>
            <span style={{ fontSize: 13, color: c }}>{fmt(v)}</span>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>Payment mode breakdown</div>
        {["Cash", "UPI", "Credit"].map(m => {
          const ms = filtSales.filter(s => s.mode === m);
          if (!ms.length) return null;
          return (
            <div key={m} style={S.row}>
              <span style={{ fontSize: 13, display: "flex", alignItems: "center" }}>
                <PayPill mode={m} /> {m}
              </span>
              <strong style={{ fontSize: 13 }}>{fmt(ms.reduce((a, s) => a + s.amount, 0))}</strong>
            </div>
          );
        })}
        {filtSales.length === 0 && <EmptyState text="No data" />}
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>Sales by oil type</div>
        {OILS.map(o => {
          const os = filtSales.filter(s => s.oil === o);
          if (!os.length) return null;
          return (
            <div key={o} style={S.row}>
              <div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <OilDot oil={o} />
                  <span style={{ fontSize: 13 }}>{o}</span>
                </div>
                <div style={{ fontSize: 11, color: "#aaa", paddingLeft: 14 }}>
                  {fmtL(os.reduce((a, s) => a + s.litres, 0))} sold
                </div>
              </div>
              <strong style={{ fontSize: 13 }}>{fmt(os.reduce((a, s) => a + s.amount, 0))}</strong>
            </div>
          );
        })}
        {filtSales.length === 0 && <EmptyState text="No sales data for this period" />}
      </div>

      {filtCake.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>Cake sales breakdown</div>
          {OILS.map(o => {
            const cs = filtCake.filter(s => s.oil === o);
            if (!cs.length) return null;
            return (
              <div key={o} style={S.row}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <OilDot oil={o} /><span style={{ fontSize: 13 }}>{o} cake</span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{fmt(cs.reduce((a, s) => a + s.amount, 0))}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{fmtKg(cs.reduce((a, s) => a + s.kg, 0))}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── PRICES ───────────────────────────────────────────────────────────────────

export function Prices({ state, actions, user }) {
  const [local, setLocal] = useState(() => JSON.parse(JSON.stringify(state.prices)));
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const update = (type, oil, sku, val) => {
    setLocal(prev => {
      const np = JSON.parse(JSON.stringify(prev));
      if (type === "raw")  np.raw[oil]       = Number(val);
      if (type === "cake") np.cake[oil]       = Number(val);
      if (type === "oil")  np.oil[oil][sku]   = Number(val);
      return np;
    });
  };

  const save = async () => {
    setSaving(true);
    await actions.updatePrices(local, user);
    setMsg("All prices saved!"); setTimeout(() => setMsg(""), 2500);
    setSaving(false);
  };

  return (
    <div style={{ padding: "12px 14px" }}>
      <AlertBar
        text="Price changes apply to all future valuations and bill calculations."
        type="info"
      />
      {OILS.map(o => (
        <div key={o} style={S.card}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <OilDot oil={o} size={10} />
            <strong style={{ fontSize: 14 }}>{o}</strong>
          </div>

          <label style={S.label}>Raw material (₹/kg)</label>
          <input style={S.input} type="number" value={local.raw[o] || ""}
            onChange={e => update("raw", o, null, e.target.value)} />

          <label style={S.label}>Cake (₹/kg)</label>
          <input style={S.input} type="number" value={local.cake[o] || ""}
            onChange={e => update("cake", o, null, e.target.value)} />

          <label style={S.label}>Oil prices per pack size (₹)</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {SKUS.map(s => (
              <div key={s}>
                <label style={{ ...S.label, marginBottom: 2 }}>{s}</label>
                <input style={{ ...S.input, marginBottom: 0 }} type="number"
                  value={local.oil?.[o]?.[s] || ""}
                  onChange={e => update("oil", o, s, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <SaveMsg msg={msg} />
      <button style={{ ...S.btn(), marginTop: 4 }} onClick={save} disabled={saving}>
        {saving ? "Saving..." : "Save All Prices"}
      </button>
    </div>
  );
}
